import React, { useMemo } from 'react'
import {
  Banner,
  BlockStack,
  Box,
  Button,
  Card,
  Divider,
  InlineGrid,
  Link,
  ProgressBar,
  Text,
  ExceptionList,
  List,
} from '@shopify/polaris'
import { InfoIcon } from '@shopify/polaris-icons'
import { useTranslation } from 'react-i18next'
import { numberFormater } from '../../../utils/formater'
import numeral from 'numeral'
import { PlanSlugs } from '../../../constants/enums'
import {
  calculateServesUsage,
  formatBillingPeriod,
  formatDate,
} from './accountHelpers'

/**
 * Account card component for SERVES pricing strategy
 * Handles both trial and active subscription states
 */
const ServesAccountCard = ({
  config,
  billingCycle,
  shopPlan,
  dashboardData,
  pricing,
  onOpenBilling,
}) => {
  const { t } = useTranslation()
  const isTrialPeriod = billingCycle?.isTrialPeriod || false

  // Get the current plan details
  const currentPlanDetails = shopPlan?.planDetails || shopPlan
  
  // Calculate usage data
  const usage = useMemo(() => calculateServesUsage(billingCycle, shopPlan), [billingCycle, shopPlan])
  
  // Calculate additional charge for usage-based plans
  const usageBasedCharge = useMemo(() => {
    const showUsageBasedAmount = shopPlan?.isUnlimited || shopPlan?.isExtendedUsageEnabled
    if (!showUsageBasedAmount) return null
    
    const maxPrice = parseFloat(
      (
        (parseFloat(shopPlan?.recommendationServesBatchPrice || 0) *
          (parseInt(shopPlan?.recommendationsServedHardLimit || 0) -
            parseInt(shopPlan?.maximumRecommendationsServed || 0))) /
        parseInt(shopPlan?.recommendationServesBatchSize || 1)
      ).toFixed(2),
    )
    
    return numeral(
      shopPlan?.isUnlimited
        ? Math.min(
            parseFloat(billingCycle?.billingPrice || 0),
            parseFloat(billingCycle?.maximumPrice || 0),
          )
        : Math.min(parseFloat(billingCycle?.billingPrice || 0), maxPrice),
    ).format('0.00')
  }, [billingCycle, shopPlan])
  
  // Calculate usage charge rule text
  const usageBasedChargeRule = useMemo(() => {
    const showUsageBasedAmount = shopPlan?.isUnlimited || shopPlan?.isExtendedUsageEnabled
    if (!showUsageBasedAmount) return null
    
    const extraUsage = Math.max(
      0,
      (billingCycle?.currentUsage || 0) - (shopPlan?.maximumRecommendationsServed || 0),
    )
    
    return shopPlan?.isUnlimited
      ? t('SettingsPage.Sections.Account.Card.Usage.usageChargeRulePercent', {
          percent: `${billingCycle?.revenuePercent || 0}%`,
        })
      : t('SettingsPage.Sections.Account.Card.Usage.usageChargeRuleAdditonal', {
          usage: extraUsage > 0 ? numberFormater(extraUsage) : '',
          price: shopPlan?.recommendationServesBatchPrice || 0,
          batchSize: numberFormater(shopPlan?.recommendationServesBatchSize || 0),
        })
  }, [billingCycle, shopPlan, t])
  
  // Combined billing configuration
  const combinedBilling = dashboardData?.config?.extra?.billing || {}
  const combinedBillingEnabled = combinedBilling.combinedBillingEnabled || false
  const isParentStore = combinedBilling.parentStore === dashboardData?.shop?.myshopifyDomain
  const childrenStore = combinedBilling.childrenStore || []
  const isChildStore = childrenStore.includes(dashboardData?.shop?.myshopifyDomain)
  

  // Show usage about to surpass warning
  const usageAboutToSurpass = usage.percentageOfHardLimit > 80 && usage.percentageOfHardLimit < 100
  console.log('usage', usage, currentPlanDetails, billingCycle)
  return (
    <>
      <BlockStack gap="500">
        {/* Banners */}
        <BlockStack>
          {isTrialPeriod && (
            <Banner tone="warning">
              <p>
                {t('SettingsPage.Sections.Account.trialTemplates', {
                  time: formatDate(billingCycle.endingOn)
                })}
              </p>
            </Banner>
          )}
          {usageAboutToSurpass && (
            <Banner tone="warning">
              <p>
                {t('SettingsPage.Sections.Account.usageAboutToSurpass')}
                <Button
                  onClick={() => pricing.open({ abovePlans: dashboardData.shopPlan })}
                >
                  {t('SettingsPage.Sections.Account.upgradeBtnCta')}
                </Button>
              </p>
            </Banner>
          )}
        </BlockStack>
        
        <Card padding="0">
          {/* Combined Billing Child Store Notice */}
          {combinedBillingEnabled && isChildStore && (
            <>
              <Box padding="400">
                <ExceptionList
                  items={[
                    {
                      icon: InfoIcon,
                      description: t(
                        'SettingsPage.Sections.Account.Card.Usage.combinedBillingChild',
                        {
                          shop: (
                            <Link url={'https://'+combinedBilling.parentStore} target="_blank" monochrome removeUnderline>
                              {combinedBilling.parentStore}
                            </Link>
                          ),
                        },
                      ),
                    },
                  ]}
                />
              </Box>
              <Divider />
            </>
          )}
          
          {/* Plan Header */}
          <Box padding="400">
            <BlockStack gap="300">
              {usage.isOverLimit && (
                <Banner tone="critical">
                  <Text>
                    {t('SettingsPage.Sections.Account.Card.Usage.usageSurpassed')}
                  </Text>
                </Banner>
              )}
              
              <InlineGrid columns="1fr auto auto" gap="300" alignItems="center">
                <BlockStack>
                  <Text as="strong">
                    {isTrialPeriod
                      ? t('SettingsPage.Sections.Account.Card.Usage.trialPlanText', {
                          planName: currentPlanDetails?.name || config.planName,
                        })
                      : t('SettingsPage.Sections.Account.Card.Usage.titleTemplate', {
                          planName: currentPlanDetails?.name || config.planName,
                        })
                    }
                  </Text>
                  <Text as="p">
                    {t('SettingsPage.Sections.Account.Card.Usage.description')}
                  </Text>
                </BlockStack>
                {config.upgradeRequired && (
                  <div>
                    <Button url="/pricing" primary>
                      {t('SettingsPage.Sections.Account.upgradeBtnCta')}
                    </Button>
                  </div>
                )}
                {config.onPaidPlan && (
                  <Link monochrome url="/pricing">
                    {t('DefaultText.downgradeText')}
                  </Link>
                )}
              </InlineGrid>
            </BlockStack>
          </Box>
          
          {/* Combined Billing Parent Store */}
          {isParentStore && childrenStore.length > 0 && (
            <>
              <Divider />
              <Box padding="400">
                <BlockStack gap="050">
                  <Text>
                    {t('SettingsPage.Sections.Account.Card.Usage.combinedBilling')}
                  </Text>
                  <List>
                    {childrenStore.map((store) => (
                      <List.Item key={store}>
                        <Link
                          target="_blank"
                          url={'https://'+store}
                          monochrome
                          removeUnderline
                        >
                          {store}
                        </Link>
                      </List.Item>
                    ))}
                  </List>
                </BlockStack>
              </Box>
            </>
          )}
          
          {/* Billing Details */}
          {(!combinedBillingEnabled || isParentStore) && (
            <>
              <Divider />
              <Box padding="400">
                <InlineGrid columns="1fr 1fr 1fr" gap="200">
                  {!shopPlan?.onAnnual && (
                    <BlockStack gap="100">
                      <Text>
                        {t('SettingsPage.Sections.Account.Card.Usage.monthlyCharge')}
                      </Text>
                      <Text as="strong">${shopPlan?.price || '0.00'}</Text>
                    </BlockStack>
                  )}
                  {shopPlan?.onAnnual && (
                    <BlockStack gap="100">
                      <Text>
                        {t('SettingsPage.Sections.Account.Card.Usage.annualCharge')}
                      </Text>
                      <Text as="strong">${shopPlan?.annualPrice || '0.00'}</Text>
                    </BlockStack>
                  )}
                  {!shopPlan?.isUnlimited && !shopPlan?.onAnnual && usageBasedCharge && (
                    <BlockStack gap="100">
                      <Text>
                        {t('SettingsPage.Sections.Account.Card.Usage.additionalUsageCharge')}
                      </Text>
                      <Text as="strong">${usageBasedCharge}</Text>
                    </BlockStack>
                  )}
                  {!isTrialPeriod && (
                    <BlockStack gap="100">
                      <Text>
                        {t('SettingsPage.Sections.Account.Card.Usage.billingPeriod')}
                      </Text>
                      <Text as="strong">{formatBillingPeriod(billingCycle)}</Text>
                    </BlockStack>
                  )}
                </InlineGrid>
                {usageBasedChargeRule && (
                  <Box paddingBlockStart="200">
                    <Text variant="bodySm" tone="subdued">
                      {usageBasedChargeRule}
                    </Text>
                  </Box>
                )}
              </Box>
            </>
          )}
          
          {/* Usage Progress */}
          <Divider />
          <Box padding="400">
            <BlockStack gap="300">
              <Box>
                <Text as="strong">
                  {t('SettingsPage.Sections.Account.Card.Usage.consumption', {
                    consumedPercentage: (usage.percentageUsed).toFixed(0) + '%',
                    consumed: numberFormater(usage.currentUsage),
                    total: numberFormater(usage.hardLimit),
                  })}
                </Text>
                {!isTrialPeriod ? (
                  shopPlan?.isUsageBased ? (
                    <Text>
                      {t('SettingsPage.Sections.Account.Card.Usage.postCharge', {
                        limit: numberFormater(usage.hardLimit),
                        total: numberFormater(usage.maxUsage),
                      })}
                    </Text>
                  ) : (
                    <Text>
                      {t('SettingsPage.Sections.Account.Card.Usage.recStop')}
                    </Text>
                  )
                ) : (
                  <Text>
                    {t('SettingsPage.Sections.Account.Card.Usage.trialCharge', {
                      date: formatDate(billingCycle.endingOn),
                      limit: numberFormater(usage.maxUsage),
                    })}
                  </Text>
                )}
              </Box>
              <ProgressBar
                size="small"
                tone="primary"
                progress={usage.percentageUsed}
              />
            </BlockStack>
          </Box>
          
          {/* Billing Management */}
          {(!combinedBillingEnabled || isParentStore) && (
            <>
              <Divider />
              <Box padding="400">
                <BlockStack>
                  <Text as="strong">
                    {t('SettingsPage.Sections.Account.Card.Billing.title')}
                  </Text>
                  <InlineGrid columns="1fr auto" gap="100">
                    <Text>
                      {t('SettingsPage.Sections.Account.Card.Billing.description')}
                    </Text>
                    <Button onClick={onOpenBilling}>
                      {t('SettingsPage.Sections.Account.Card.Billing.btnCta')}
                    </Button>
                  </InlineGrid>
                </BlockStack>
              </Box>
            </>
          )}
        </Card>
      </BlockStack>
    </>
  )
}

export default ServesAccountCard